import { ROOM_ORDER, ROOM_THEMES } from './roomThemes';

const baseRooms = [
  {
    id: 'innovation-lab',
    name: 'Innovation Lab',
    slug: 'innovation-lab',
    description: 'Where visionaries and disruptors gather to reshape the future',
    tagline: 'Think Different. Build Tomorrow.',
    theme: {
      primary_color: '#0EA5E9',
      secondary_color: '#1E3A5F',
      glow_color: '#38BDF8',
      background_image: null,
      pattern: 'circuit',
      ambient: 'cool',
    },
    mind_ids: ['steve-jobs', 'alan-turing', 'ada-lovelace'],
    status: 'active',
    is_custom: false,
    order: 1,
  },
  {
    id: 'philosophy-hall',
    name: 'Philosophy Hall',
    slug: 'philosophy-hall',
    description: "The eternal forum where humanity's deepest questions are explored",
    tagline: 'The Unexamined Life Is Not Worth Living',
    theme: {
      primary_color: '#7C3AED',
      secondary_color: '#3B1E54',
      glow_color: '#A78BFA',
      background_image: null,
      pattern: 'marble',
      ambient: 'warm',
    },
    mind_ids: ['socrates', 'aristotle', 'simone-de-beauvoir'],
    status: 'active',
    is_custom: false,
    order: 2,
  },
  {
    id: 'british-greats',
    name: 'British Greats',
    slug: 'british-greats',
    description: "The island's finest minds who shaped science, literature, and leadership",
    tagline: 'From These Shores, To The World',
    theme: {
      primary_color: '#DC2626',
      secondary_color: '#1E3A5F',
      glow_color: '#FFD700',
      background_image: null,
      pattern: 'heraldic',
      ambient: 'warm',
    },
    mind_ids: ['winston-churchill', 'charles-darwin', 'william-shakespeare'],
    status: 'active',
    is_custom: false,
    order: 3,
  },
  {
    id: 'science-academy',
    name: 'Science Academy',
    slug: 'science-academy',
    description: 'Where the laws of nature are discovered and understood',
    tagline: 'Standing On The Shoulders Of Giants',
    theme: {
      primary_color: '#10B981',
      secondary_color: '#064E3B',
      glow_color: '#34D399',
      background_image: null,
      pattern: 'molecular',
      ambient: 'cool',
    },
    mind_ids: ['albert-einstein', 'marie-curie', 'isaac-newton'],
    status: 'active',
    is_custom: false,
    order: 4,
  },
  {
    id: 'creative-studio',
    name: 'Creative Studio',
    slug: 'creative-studio',
    description: 'Masters of art, music, and creative expression across the ages',
    tagline: 'Every Artist Was First An Amateur',
    theme: {
      primary_color: '#F59E0B',
      secondary_color: '#78350F',
      glow_color: '#FBBF24',
      background_image: null,
      pattern: 'paint-splatter',
      ambient: 'warm',
    },
    mind_ids: ['leonardo-da-vinci', 'frida-kahlo', 'david-bowie'],
    status: 'active',
    is_custom: false,
    order: 5,
  },
  {
    id: 'world-shapers',
    name: 'World Shapers',
    slug: 'world-shapers',
    description: 'Leaders and activists who bent the arc of history toward justice',
    tagline: 'Be The Change You Wish To See',
    theme: {
      primary_color: '#8B5A2B',
      secondary_color: '#3D2914',
      glow_color: '#D4A574',
      background_image: null,
      pattern: 'globe',
      ambient: 'warm',
    },
    mind_ids: ['mahatma-gandhi', 'nelson-mandela', 'martin-luther-king'],
    status: 'active',
    is_custom: false,
    order: 6,
  },
];

const baseMinds = [
  {
    id: 'steve-jobs',
    name: 'Steve Jobs',
    title: 'Visionary of the Digital Age',
    era: '1955-2011',
    description: 'Co-founder of Apple, pioneer of personal computing, and master of design thinking.',
    style: 'visionary',
    specialty: 'Technology, Design, Innovation, Leadership',
    thinking_message: 'Steve is connecting the dots...',
    room_id: 'innovation-lab',
    portrait: '/portraits/steve-jobs.jpg',
  },
  {
    id: 'alan-turing',
    name: 'Alan Turing',
    title: 'Father of Computer Science',
    era: '1912-1954',
    description: 'Mathematician who cracked Enigma and laid the foundations for modern computing and AI.',
    style: 'analytical',
    specialty: 'Mathematics, Cryptography, Computer Science, Artificial Intelligence',
    thinking_message: 'Turing is computing possibilities...',
    room_id: 'innovation-lab',
    portrait: '/portraits/alan-turing.jpg',
  },
  {
    id: 'ada-lovelace',
    name: 'Ada Lovelace',
    title: 'First Computer Programmer',
    era: '1815-1852',
    description: 'Visionary mathematician who saw the potential of computing machines beyond mere calculation.',
    style: 'imaginative',
    specialty: 'Mathematics, Computing Theory, Poetry of Science',
    thinking_message: 'Ada is weaving numbers and imagination...',
    room_id: 'innovation-lab',
    portrait: '/portraits/ada-lovelace.jpg',
  },
  {
    id: 'socrates',
    name: 'Socrates',
    title: 'Father of Western Philosophy',
    era: '470-399 BCE',
    description: 'Athenian philosopher known for the Socratic method of questioning to illuminate ideas.',
    style: 'interrogative',
    specialty: 'Ethics, Epistemology, Dialectic',
    thinking_message: 'Socrates is contemplating your question...',
    room_id: 'philosophy-hall',
    portrait: '/portraits/socrates.jpg',
  },
  {
    id: 'aristotle',
    name: 'Aristotle',
    title: 'The Philosopher',
    era: '384-322 BCE',
    description: 'Student of Plato, tutor of Alexander, founder of logic and systematic philosophy.',
    style: 'systematic',
    specialty: 'Logic, Metaphysics, Ethics, Natural Science',
    thinking_message: 'Aristotle is organizing categories...',
    room_id: 'philosophy-hall',
    portrait: '/portraits/aristotle.jpg',
  },
  {
    id: 'simone-de-beauvoir',
    name: 'Simone de Beauvoir',
    title: 'Mother of Modern Feminism',
    era: '1908-1986',
    description: 'Existentialist philosopher who examined gender, freedom, and the human condition.',
    style: 'analytical',
    specialty: 'Existentialism, Feminism, Ethics, Literature',
    thinking_message: 'Simone is examining the situation...',
    room_id: 'philosophy-hall',
    portrait: '/portraits/simone-de-beauvoir.jpg',
  },
  {
    id: 'winston-churchill',
    name: 'Winston Churchill',
    title: 'The British Bulldog',
    era: '1874-1965',
    description: 'Wartime Prime Minister, orator, and Nobel laureate who rallied Britain through its darkest hour.',
    style: 'oratorical',
    specialty: 'Leadership, History, Strategy, Writing',
    thinking_message: 'Churchill is marshalling his words...',
    room_id: 'british-greats',
    portrait: '/portraits/winston-churchill.jpg',
  },
  {
    id: 'charles-darwin',
    name: 'Charles Darwin',
    title: 'Father of Evolution',
    era: '1809-1882',
    description: 'Naturalist whose theory of evolution by natural selection transformed our understanding of life.',
    style: 'observational',
    specialty: 'Biology, Natural History, Evolution, Geology',
    thinking_message: 'Darwin is examining the evidence...',
    room_id: 'british-greats',
    portrait: '/portraits/charles-darwin.jpg',
  },
  {
    id: 'william-shakespeare',
    name: 'William Shakespeare',
    title: 'The Bard of Avon',
    era: '1564-1616',
    description: 'Playwright and poet whose works defined the English language and explored the human condition.',
    style: 'poetic',
    specialty: 'Drama, Poetry, Human Nature, Language',
    thinking_message: 'Shakespeare is searching for the right words...',
    room_id: 'british-greats',
    portrait: '/portraits/william-shakespeare.jpg',
  },
  {
    id: 'albert-einstein',
    name: 'Albert Einstein',
    title: 'Father of Modern Physics',
    era: '1879-1955',
    description: 'Theoretical physicist who revolutionized our understanding of space, time, and energy.',
    style: 'imaginative',
    specialty: 'Physics, Relativity, Quantum Theory, Philosophy of Science',
    thinking_message: 'Einstein is riding a beam of light...',
    room_id: 'science-academy',
    portrait: '/portraits/albert-einstein.jpg',
  },
  {
    id: 'marie-curie',
    name: 'Marie Curie',
    title: 'Pioneer of Radioactivity',
    era: '1867-1934',
    description: 'First woman to win a Nobel Prize, and only person to win in two different sciences.',
    style: 'empirical',
    specialty: 'Physics, Chemistry, Radioactivity, Scientific Method',
    thinking_message: 'Marie is analyzing the data...',
    room_id: 'science-academy',
    portrait: '/portraits/marie-curie.jpg',
  },
  {
    id: 'isaac-newton',
    name: 'Isaac Newton',
    title: 'Architect of Classical Physics',
    era: '1643-1727',
    description: 'Mathematician and physicist who formulated the laws of motion and universal gravitation.',
    style: 'precise',
    specialty: 'Physics, Mathematics, Optics, Calculus',
    thinking_message: 'Newton is calculating the forces...',
    room_id: 'science-academy',
    portrait: '/portraits/isaac-newton.jpg',
  },
  {
    id: 'leonardo-da-vinci',
    name: 'Leonardo da Vinci',
    title: 'The Universal Genius',
    era: '1452-1519',
    description: 'Renaissance polymath who excelled in art, science, engineering, and anatomy.',
    style: 'observational',
    specialty: 'Art, Anatomy, Engineering, Invention, Natural Philosophy',
    thinking_message: 'Leonardo is sketching possibilities...',
    room_id: 'creative-studio',
    portrait: '/portraits/leonardo-da-vinci.jpg',
  },
  {
    id: 'frida-kahlo',
    name: 'Frida Kahlo',
    title: 'Painter of Pain and Passion',
    era: '1907-1954',
    description: 'Mexican artist known for unflinching self-portraits and exploration of identity, pain, and culture.',
    style: 'passionate',
    specialty: 'Painting, Self-Expression, Mexican Culture, Surrealism',
    thinking_message: 'Frida is painting with her soul...',
    room_id: 'creative-studio',
    portrait: '/portraits/frida-kahlo.jpg',
  },
  {
    id: 'david-bowie',
    name: 'David Bowie',
    title: 'The Starman',
    era: '1947-2016',
    description: 'Musical chameleon who constantly reinvented himself and pushed artistic boundaries.',
    style: 'provocative',
    specialty: 'Music, Performance, Visual Art, Reinvention',
    thinking_message: 'Bowie is changing costumes...',
    room_id: 'creative-studio',
    portrait: '/portraits/david-bowie.jpg',
  },
  {
    id: 'mahatma-gandhi',
    name: 'Mahatma Gandhi',
    title: 'Father of the Nation',
    era: '1869-1948',
    description: 'Leader of Indian independence through nonviolent civil disobedience.',
    style: 'gentle',
    specialty: 'Nonviolence, Civil Rights, Spirituality, Leadership',
    thinking_message: 'Gandhi is seeking the path of truth...',
    room_id: 'world-shapers',
    portrait: '/portraits/mahatma-gandhi.jpg',
  },
  {
    id: 'nelson-mandela',
    name: 'Nelson Mandela',
    title: 'Madiba',
    era: '1918-2013',
    description: "Anti-apartheid revolutionary who became South Africa's first Black president.",
    style: 'reconciliatory',
    specialty: 'Leadership, Reconciliation, Human Rights, Perseverance',
    thinking_message: 'Mandela is considering the long walk...',
    room_id: 'world-shapers',
    portrait: '/portraits/nelson-mandela.jpg',
  },
  {
    id: 'martin-luther-king',
    name: 'Martin Luther King Jr.',
    title: 'Voice of the Dream',
    era: '1929-1968',
    description: 'Civil rights leader who championed nonviolent resistance and racial equality.',
    style: 'oratorical',
    specialty: 'Civil Rights, Nonviolent Protest, Theology, Oratory',
    thinking_message: 'Dr. King is dreaming of justice...',
    room_id: 'world-shapers',
    portrait: '/portraits/martin-luther-king.jpg',
  },
];

const roomOrderIndex = new Map(ROOM_ORDER.map((id, index) => [id, index]));

const clone = (value) => JSON.parse(JSON.stringify(value));

const sortRooms = (rooms) => [...rooms].sort((a, b) => {
  const aOrder = roomOrderIndex.has(a.id) ? roomOrderIndex.get(a.id) : 999;
  const bOrder = roomOrderIndex.has(b.id) ? roomOrderIndex.get(b.id) : 999;
  return aOrder - bOrder || (a.order || 0) - (b.order || 0);
});

export const getStaticRooms = () => sortRooms(baseRooms).map(room => ({
  ...clone(room),
  theme: {
    ...clone(room.theme),
    ...(ROOM_THEMES[room.id] || {}),
  },
}));

export const getStaticMinds = () => clone(baseMinds);

export const getStaticMindsByRoom = (roomId) => (
  getStaticMinds().filter(mind => mind.room_id === roomId)
);

export const getStaticMindById = (mindId) => (
  getStaticMinds().find(mind => mind.id === mindId) || null
);

export const getStaticMindsPreview = (roomId) => (
  getStaticMindsByRoom(roomId).slice(0, 3)
);

export const hasStaticMuseumData = true;
